import { BOTONES } from '../../componentes/botones';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import {
  Component,
  Input,
  TrackByFunction,
  OnInit,
  OnChanges,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';

import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { HelperService } from '../servicios/helper.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SectionTitleComponent } from '../../componentes/section-title/section-title.component';
import { sanitizarHtml } from '../html-seguro.util';

export interface CardData {
  [key: string]: string | null;
}

export interface AlternativeValues {
  [key: string]: string;
}

@Component({
  selector: 'app-card',
  standalone: true,
  imports: [TooltipDirective, MatTableModule, MatIconModule, SectionTitleComponent, BOTONES],
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent implements OnInit, OnChanges {
  private helper = inject(HelperService);
  private sanitizer = inject(DomSanitizer);

  @Input() title: string = '';
  @Input() icon: string;
  @Input() centerTitle: boolean = false; // Nueva propiedad para centrar el título
  @Input() type: 'carousel' | 'horizontal-table' | 'vertical-table' | 'text' =
    'text';

  //Para carrusel
  @Input() images: string[] = [];
  @Input() width: string = '100%';
  @Input() height: string = 'auto';

  //Para tabla horizontal
  @Input() data: { [key: string]: string } = {};

  //Para tabla vertical
  @Input() headers: string[] = [];
  @Input() rows: string[][] = [];
  @Input() objectRows: { [key: string]: any }[] = [];
  @Input() rowOrder: string[] = [];
  @Input() headerLabels: { [key: string]: string } = {};

  //Para texto
  @Input() text: string = '';
  @Input() noInfo: boolean = false;

  //Para tabla vertical y horizontaL
  /**
   * Muestra o no el box-shadow de las tablas
   */
  @Input() boxShadow = true;
  @Input() alternativeValues: { [key: string]: string } = {}; // Objeto para valores alternativos específicos

  //Para botón de copia al portapapeles
  @Input() copyableFields: string[] = []; // Nombres de los campos que deben tener botón de copia

  currentImageIndex = 0;

  // Propiedades para almacenar los datos procesados
  horizontalDataSourceCache: { key: string; value: string }[] = [];
  verticalDataSourceCache: any[] = [];
  verticalDisplayedColumnsCache: string[] = [];
  horizontalDisplayedColumnsCache: string[] = ['key', 'value'];

  // TrackBy functions
  trackByColumn: TrackByFunction<string> = (index: number, item: string) =>
    item;
  trackByRow: TrackByFunction<any> = (index: number, item: any) => index;

  ngOnInit() {
    this.updateDataSources();
  }

  ngOnChanges() {
    this.updateDataSources();
  }

  private updateDataSources() {
    // Actualizar horizontal data source
    this.horizontalDataSourceCache = Object.keys(this.data || {}).map(
      (key) => ({
        key,
        value: this.data[key],
      }),
    );

    // Actualizar vertical displayed columns
    this.verticalDisplayedColumnsCache = this.rowOrder.length
      ? this.rowOrder
      : this.headers;

    // Actualizar vertical data source
    if (this.objectRows.length && this.rowOrder.length) {
      this.verticalDataSourceCache = this.objectRows.map((obj) => {
        const rowObj: any = {};
        this.rowOrder.forEach((key) => {
          const value = obj[key];
          rowObj[key] =
            value !== undefined && value !== null ? value.toString() : null;
        });
        return rowObj;
      });
    } else {
      this.verticalDataSourceCache = this.rows.map((row) => {
        const rowObj: any = {};
        this.headers.forEach((header, i) => {
          rowObj[header] = row[i];
        });
        return rowObj;
      });
    }
  }

  // Permite estilos (el sanitizado de Angular los saca); DOMPurify saca scripts
  // y handlers.
  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(sanitizarHtml(html));
  }

  // Obtenemos las claves de los datos
  get dataKeys(): string[] {
    return Object.keys(this.data || {});
  }

  // Para la tabla horizontal (clave-valor)
  get horizontalDataSource(): { key: string; value: string }[] {
    return this.horizontalDataSourceCache;
  }

  get horizontalDisplayedColumns(): string[] {
    return this.horizontalDisplayedColumnsCache;
  }

  // Para la tabla vertical
  get verticalDisplayedColumns(): string[] {
    return this.verticalDisplayedColumnsCache;
  }

  get verticalDataSource(): any[] {
    return this.verticalDataSourceCache;
  }

  prevImage() {
    this.currentImageIndex =
      (this.currentImageIndex - 1 + this.images.length) % this.images.length;
  }

  nextImage() {
    this.currentImageIndex = (this.currentImageIndex + 1) % this.images.length;
  }

  // Método para verificar si un campo debe tener botón de copia
  isCopyableField(fieldKey: string): boolean {
    return this.copyableFields.includes(fieldKey);
  }

  // Método para copiar al portapapeles
  async copyToClipboard(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      // Aquí podrías agregar una notificación visual opcional
      this.helper.notifSuccess(`Copiado al portapapeles`);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
      // Fallback para navegadores más antiguos
      this.fallbackCopyTextToClipboard(value);
    }
  }

  // Método de respaldo para navegadores sin soporte para navigator.clipboard
  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Error al copiar al portapapeles (fallback):', err);
    }

    document.body.removeChild(textArea);
  }
}
